import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useLocation } from 'wouter';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { getDateLocale, getDateFormats } from '@/utils/dateLocale';
import { Calendar, Clock, User, AlertCircle, CheckCircle, XCircle, Loader2 } from 'lucide-react';

import apiClient from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { LanguagePill } from '@/components/chrome/LanguagePill';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface GuestBooking {
  id: number;
  status: string;
  customer_first_name: string;
  customer_last_name: string;
  customer_full_name: string;
  customer_email: string;
  customer_phone: string;
  notes: string;
  start_time: string;
  end_time: string;
  confirmed_at: string | null;
  cancelled_at: string | null;
  cancellation_reason: string | null;
  service: {
    id: number;
    name: string;
    description: string;
    duration_minutes: number;
    price: number;
  };
  provider: {
    id: number;
    name: string;
    title: string;
    bio: string;
  };
  organization: {
    id: number;
    name: string;
    email: string;
    phone: string;
    address: string;
  };
  token_expires_at: string;
  created_at: string;
  updated_at: string;
}

interface GuestBookingResponse {
  booking: GuestBooking;
  can_cancel: boolean;
  can_reschedule: boolean;
}

export default function GuestBookingCancelPage() {
  const { token } = useParams<{ token: string }>();
  const [_, setLocation] = useLocation();
  const { t } = useTranslation('guestBooking');
  const [cancellationReason, setCancellationReason] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const queryClient = useQueryClient();

  // Fetch booking details using guest token
  const { data, isLoading, error } = useQuery<GuestBookingResponse>({
    queryKey: ['guest-booking', token],
    queryFn: async () => {
      const response = await apiClient.get(`/v1/guest/bookings/${token}`);
      return response.data.data;
    },
    enabled: !!token,
    retry: false,
  });

  // Cancel booking mutation
  const cancelMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.post(`/v1/guest/bookings/${token}/cancel`, {
        reason: cancellationReason || 'No reason provided',
      });
      return response.data.data;
    },
    onSuccess: () => {
      // Invalidate and refetch booking data
      queryClient.invalidateQueries({ queryKey: ['guest-booking', token] });
      setShowSuccess(true);
    },
  });

  const handleCancel = () => {
    setShowConfirmDialog(true);
  };

  const handleConfirmCancel = () => {
    setShowConfirmDialog(false);
    cancelMutation.mutate();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-surface-2 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
              <p className="text-soft">{t('cancel.loading')}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-surface-2 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto" />
              <div>
                <h3 className="text-h3 text-ink">{t('cancel.bookingNotFound')}</h3>
                <p className="text-soft">
                  {t('cancel.bookingNotFoundDescription')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { booking, can_cancel } = data;
  const startTime = parseISO(booking.start_time);

  // Show success state after cancellation
  if (showSuccess || booking.status === 'cancelled') {
    return (
      <div className="min-h-screen bg-surface-2 py-8 px-4">
        <div className="max-w-3xl mx-auto space-y-6">
          <div className="flex justify-end">
            <LanguagePill />
          </div>
          <div className="text-center space-y-2">
            <h1 className="text-h1 text-ink">{booking.organization.name}</h1>
            <p className="text-soft">{t('cancel.appointmentCancelled')}</p>
          </div>

          <Card>
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
                <div>
                  <h2 className="text-h1 text-ink mb-2">{t('cancel.successTitle')}</h2>
                  <p className="text-soft">
                    {t('cancel.successDescription')}
                  </p>
                </div>

                <Separator className="my-6" />

                <div className="text-left space-y-3">
                  <h3 className="font-semibold">{t('cancel.cancelledAppointmentDetails')}</h3>
                  <div className="space-y-2 text-sm text-soft">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      <span>{format(startTime, getDateFormats().fullWithWeekday, { locale: getDateLocale() })}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      <span>{format(startTime, 'HH:mm')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      <span>{t('cancel.serviceWith', { service: booking.service.name, provider: booking.provider.name })}</span>
                    </div>
                  </div>
                </div>

                <Separator className="my-6" />

                <div className="space-y-3">
                  <p className="text-sm text-soft">
                    {t('cancel.bookAgain')}
                  </p>
                  <Button
                    onClick={() => setLocation(`/guest/bookings/${token}`)}
                    variant="outline"
                    className="w-full"
                  >
                    {t('cancel.viewBookingDetails')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Show cannot cancel message
  if (!can_cancel) {
    return (
      <div className="min-h-screen bg-surface-2 py-8 px-4">
        <div className="max-w-3xl mx-auto space-y-6">
          <div className="flex justify-end">
            <LanguagePill />
          </div>
          <div className="text-center space-y-2">
            <h1 className="text-h1 text-ink">{booking.organization.name}</h1>
            <p className="text-soft">{t('cancel.title')}</p>
          </div>

          <Card>
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                <AlertCircle className="h-16 w-16 text-amber-500 mx-auto" />
                <div>
                  <h2 className="text-h1 text-ink mb-2">{t('cancel.cannotCancel')}</h2>
                  <p className="text-soft">
                    {t('cancel.cannotCancelDescription')}
                  </p>
                </div>

                <Separator className="my-6" />

                <div className="space-y-3">
                  <p className="text-sm text-soft">
                    {t('cancel.contactAssistance')}
                  </p>
                  <div className="space-y-2">
                    <Button
                      onClick={() => setLocation(`/guest/bookings/${token}`)}
                      className="w-full"
                    >
                      {t('cancel.backToBookingDetails')}
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => window.location.href = `mailto:${booking.organization.email}`}
                    >
                      {t('cancel.contact', { name: booking.organization.name })}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Show cancellation form
  return (
    <div className="min-h-screen bg-surface-2 py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex justify-end">
          <LanguagePill />
        </div>
        <div className="text-center space-y-2">
          <h1 className="text-h1 text-ink">{booking.organization.name}</h1>
          <p className="text-soft">{t('cancel.title')}</p>
        </div>

        <Alert className="border-amber-200 bg-amber-50">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            {t('cancel.warningMessage')}
          </AlertDescription>
        </Alert>

        <Card>
          <CardHeader>
            <CardTitle>{t('cancel.appointmentToCancel')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-4 p-4 bg-surface-2 rounded-lg">
              <div className="flex items-center gap-3">
                <Calendar className="h-5 w-5 text-soft" />
                <div>
                  <p className="text-sm text-soft">{t('cancel.date')}</p>
                  <p className="font-medium">{format(startTime, getDateFormats().fullWithWeekday, { locale: getDateLocale() })}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-soft" />
                <div>
                  <p className="text-sm text-soft">{t('cancel.time')}</p>
                  <p className="font-medium">{format(startTime, 'HH:mm')}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <User className="h-5 w-5 text-soft" />
                <div>
                  <p className="text-sm text-soft">{t('cancel.serviceAndProvider')}</p>
                  <p className="font-medium">{booking.service.name}</p>
                  <p className="text-sm text-soft">{booking.provider.name}</p>
                </div>
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label htmlFor="reason">{t('cancel.reasonLabel')}</Label>
              <Textarea
                id="reason"
                placeholder={t('cancel.reasonPlaceholder')}
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                rows={4}
                className="resize-none"
              />
              <p className="text-xs text-soft">
                {t('cancel.reasonHint')}
              </p>
            </div>

            <Separator />

            <div className="space-y-3">
              <Button
                variant="destructive"
                className="w-full"
                onClick={handleCancel}
                disabled={cancelMutation.isPending}
              >
                {cancelMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('cancel.cancelling')}
                  </>
                ) : (
                  <>
                    <XCircle className="h-4 w-4 mr-2" />
                    {t('cancel.yesCancelAppointment')}
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setLocation(`/guest/bookings/${token}`)}
                disabled={cancelMutation.isPending}
              >
                {t('cancel.noKeepAppointment')}
              </Button>
            </div>

            {cancelMutation.isError && (
              <Alert className="border-red-200 bg-red-50">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800">
                  {t('cancel.cancelError')}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Confirm Cancel Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('cancel.confirmDialogTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('cancel.confirmDialogDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel.noKeepAppointment')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmCancel}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('cancel.yesCancelAppointment')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
